<template>
  <VContainer
    class="px-0 v2-padding"
  >
    <VRow class="v2-gutters">
      <VFadeTransition hide-on-leave>
        <template v-if="resetPassword">
          <VCol class="align" cols="12">
            <div ref="resetPanel">
              <PasswordReset
                :btn-color="btnColor"
                close-link
                @dialog:closed="resetPassword = false"
              />
            </div>
          </VCol>
        </template>
      </VFadeTransition>
      <VFadeTransition hide-on-leave>
        <template v-if="!resetPassword">
          <VCol ref="signInPanel" class="align" cols="12">
            <AuthForm
              ref="authForm"
              :btn-color="btnColor"
              :already-exists="alreadyExists"
              :demo="demo"
              :guest="guest"
              :subscription="subscription"
              :username-readonly="usernameReadonly"
              :invalid-credentials="invalidCredentials"
              :sign-in-btn-text="signInBtnText"
              :options="!cartStore.isSharedCartMode"
              :confirm-and-shop-btn="confirmAndShopBtn"
              :error="errorMessage"
              data-testid="form-sign-in"
              @form-submit="authenticate"
              @reset-password="resetPassword = true"
              @update:already-exists="emits('update:already-exists', $event)"
              @changed="invalidCredentials = false"
            >
              <template v-if="showHeaderAndFooter" #header>
                <template v-if="!cartStore.isSharedCartMode">
                  <h3 class="text-uppercase pb-1">
                    {{ header }}
                  </h3>
                  <span>{{ subHeader }}</span>
                  <br>
                  <span v-if="!demo && !subscription">{{ authMessage ? $t('nav.wishlist.dialog.p.emptyList') : $t('auth.p.welcomeBack') }}</span>
                </template>
                <div v-else>
                  <h3>{{ $t('account.signin') }}</h3>
                  {{ $t('customerCart.p.signIn') }}
                </div>
              </template>
              <template v-if="!showHeaderAndFooter && !cartStore.isSharedCartMode" #footer>
                <div />
              <!-- todo vue3 bug? if new line here with whitespace: 'preserve' in config -> compliation error              -->
              </template><template v-else-if="cartStore.isSharedCartMode" #footer>
                <SBtn link-medium class="py-4" @click="exitCart">
                  {{ $t('ida.btn.exit') }}
                </SBtn>
              </template>
            </AuthForm>
          </VCol>
        </template>
      </VFadeTransition>
    </VRow>
  </VContainer>
</template>

<script lang="ts" setup>
import AuthForm from '~/components/base/AuthForm.vue'
import PasswordReset from '~/components/auth/PasswordResetRequest.vue'
import type { Credentials } from '~/types/auth'
import { getErrorStatus, hasApiErrorCode } from '~/utils/http/api-error'
import { useAgentStore } from '~/stores/agent'
import { useAuthStore } from '~/stores/auth'
import { useCartStore } from '~/stores/cart'
import { useRootStore } from '~/stores/root'

interface Props {
  demo?: boolean
  guest?: boolean
  open?: boolean
  subscription?: boolean
  btnColor?: string
  alreadyExists?: string
  signInBtnText?: string
  usernameReadonly?: string
  authenticateAction?: (credentials: Credentials) => Promise<void>
  confirmAndShopBtn?: boolean
  showHeaderAndFooter?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  demo: false,
  guest: false,
  open: false,
  subscription: false,
  btnColor: undefined,
  alreadyExists: undefined,
  usernameReadonly: undefined,
  signInBtnText: undefined,
  confirmAndShopBtn: false,
  showHeaderAndFooter: true,
  authenticateAction: undefined
})

const emits = defineEmits<{
  (e: 'loading', loading: boolean): void
  (e: 'authenticated' | 'invalid-credentials'): void
  (e: 'authenticate', credentials: Credentials): void
  (e: 'update:already-exists', event: string): void
}>()

const { $i18n } = useNuxtApp()
const { loading, processAction } = useAction()
const { displayMessage } = useErrorText()

const errorMessage = ref('')
const resetPassword = ref(false)
const invalidCredentials = ref(false)
const authForm = useTemplateRef('authForm')
const resetPanel = useTemplateRef('resetPanel')
const signInPanel = useTemplateRef('signInPanel')
const rootStore = useRootStore()
const authMessage = computed(() => rootStore.authMessage)
const header = computed(() => {
  if (authMessage.value) {
    return $i18n.t('nav.wishlist.dialog.title.emptyList')
  }

  if (props.subscription) {
    return $i18n.t('header.subscriptionSignIn')
  }

  if (props.demo) {
    return $i18n.t('pages.auth-sign-in')
  }

  return $i18n.t('account.signin')
})
const subHeader = computed(() => {
  if (authMessage.value) {
    return ''
  }
  if (props.subscription) {
    return $i18n.t('p.subscriptionSignIn')
  }

  if (props.demo) {
    return $i18n.t('p.signInWithID')
  }

  return $i18n.t('auth.p.alreadyCustomer')
})

const authStore = useAuthStore()
const agentStore = useAgentStore()
const cartStore = useCartStore()
const router = useRouter()

const exitCart = () => {
  processAction(async () => {
    await cartStore.exitSharedCart()
    await router.push('/')
  })
}

watch(() => props.open, () => {
  // clean it on close
  if (!props.open) {
    authForm.value?.resetForm()
    invalidCredentials.value = false
  }
})
watch(() => loading.value, () => {
  emits('loading', loading.value)
})
// The v-if swap removes the control that had focus ("Forgot password" or Cancel),
// which drops keyboard focus to <body>. Move it into the panel that replaced it.
watch(resetPassword, async (showReset) => {
  await nextTick()
  const target = showReset
    ? resetPanel.value?.querySelector('input')
    : (signInPanel.value?.$el as HTMLElement | undefined)?.querySelector<HTMLElement>('#reset-link')
  target?.focus()
})

const authenticate = (credentials: Credentials) => {
  errorMessage.value = ''
  processAction(async () => {
    try {
      if (props.authenticateAction) {
        await props.authenticateAction(credentials)
      } else {
        await authStore.authenticate(credentials)
      }
      emits('authenticated')
    } catch (eRaw) {
      // An employee (@stampinup.com) address on the customer sign-in gets a 409
      // from the backend. Restart in customer mode so they get Role=Customer and
      // can check out with their staff discount — Role=Agent is blocked at
      // payment. The 409 is a signal, not a URL to follow: startOidcSignIn
      // rebuilds the correct absolute API-origin oidc-start URL. Scoped to the
      // default sign-in path; custom flows (e.g. join verification) opt out.
      if (!props.authenticateAction && getErrorStatus(eRaw) === 409) {
        agentStore.startOidcSignIn('customer')
        return
      }
      if (hasApiErrorCode(eRaw, 'SignInFailed') || hasApiErrorCode(eRaw, 'InvalidCredential')) {
        invalidCredentials.value = true
        emits('invalid-credentials')
      } else {
        // The backend's text, else the message of an Error raised for the user:
        // ConfirmAccount's authenticateAction throws the translated invalidInviteEmail
        // that way (nuxt2 fell back to e.message, PR 22436). AB#64565
        errorMessage.value = displayMessage(eRaw)
      }
    }
  })
}
</script>

<style lang="scss" scoped>
</style>
